/**
 * The settings area — `docs/catalog-and-packages.md` §6.1, redrawn from
 * `design/mockups/21-catalog-packages/html/packages-settings-home.html`.
 *
 * A nav on the left and a page on the right, under **one** page bar that
 * always reads "Settings / <page>". Everything a company sets up once and then
 * lives with is here, which is the point: before this, the catalog hung off
 * the account menu, People hung off a different part of it, and there was no
 * answer at all to "where do I change how we do things".
 *
 * The bar is the shell's, not the page's (`settings-bar.tsx`), and the crumb
 * before the title is "Settings" rather than the tenant's name. The tenant is
 * not a place you can navigate to and it is already written across the top of
 * the window; putting it in the crumb pushed the one crumb that goes somewhere
 * off to the right.
 *
 * The shell is gated on `company_settings: view`; a page may be narrower than
 * that (Catalog rides on `quotes`, People on `employees`) and checks for
 * itself. A page nobody has built yet draws a placeholder that says so.
 */
import { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { can } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { PageBar } from '../frame/page-bar.js'
import { Avatar, Button, EmptyState, Tag } from '../components/primitives.js'
import { Modal } from '../components/overlays.js'
import { IconExternal, IconMenu } from '../frame/icons.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import { formatCents, formatDocumentDate, formatNumber } from '../lib/format.js'
import { Catalog } from '../pages/catalog.js'
import { CompanyProfilePage } from '../pages/company-profile.js'
import { CatalogSetup } from '../pages/catalog-setup.js'
import { Divisions } from '../pages/divisions.js'
import { EquipmentTypes } from '../pages/equipment-types.js'
import { PackageBuilder } from '../pages/package-builder.js'
import { Organization } from '../pages/organization.js'
import { People } from '../pages/people.js'
import { ProposalSettingsPage } from '../pages/proposal-settings.js'
import { VisitKindsSettingsPage } from '../pages/visit-kinds-settings.js'
import { Roles } from '../pages/roles.js'
import { TextsAndEmailPage } from './texts-and-email.js'
import { EmailSignaturesPage } from './email-signatures.js'
import { useSettingsBar, useSettingsBarState } from './settings-bar.js'
import {
  SETTINGS_GROUPS, SETTINGS_PAGES, SettingsNav, SettingsNavSheet, canOpen, findSettingsPage,
  type SettingsGroup, type SettingsPage,
} from './settings-nav.js'
import { SalesCoveragePage } from './sales-coverage.js'
import { LeadSourcesPage } from './lead-sources.js'

export function SettingsShell({ me }: { me: MeResponse }) {
  const location = useLocation()
  const navigate = useNavigate()
  const phone = usePhone()
  const [menu, setMenu] = useState(false)
  const { bar, slot, provide } = useSettingsBarState()

  if (!can(me.permissions, 'company_settings', 'view')) {
    return (
      <EmptyState
        title="Settings is not yours to open"
        body="Company settings decide how the whole company works, so they are kept to
              the people who own that decision. Ask an owner or a branch admin."
      />
    )
  }

  const slug = location.pathname.replace(/^\/settings\/?/, '').split('/')[0] ?? ''
  const page = findSettingsPage(slug)

  /*
   * The package builder is a screen, not a settings page: it is as wide as the
   * quote builder it mirrors, and the 232px nav beside it left the rail with
   * nowhere to go (mockup `packages-builder.png` draws it full width under
   * "Settings / Catalog / Packages / <name>"). The crumbs still say where it
   * came from, so nothing is lost by dropping the column.
   */
  const wide = location.pathname.startsWith('/settings/catalog/packages/')

  // What the bar says when the open page has not asked for anything else.
  const fallback = page
    ? { title: page.label, sub: page.blurb, crumbs: undefined }
    // The home fills in "· set up <date>" itself, once it knows the date.
    : { title: 'Company settings', sub: me.tenant.name, crumbs: undefined }
  const shown = bar ?? fallback

  return (
    /*
     * `min-h-full`, because `<main>` is a scroll box rather than a flex
     * container: `flex-1` on its child means nothing, so this column was only
     * ever as tall as whatever was inside it. On a big screen with a short
     * page that left the 232px nav ending in mid-air, its border and its
     * background stopping under the last link with the page showing through
     * below. The column is now at least as tall as the area it is in, and the
     * nav stretches to it.
     */
    <div className="flex min-h-full flex-1 flex-col">
      <PageBar
        variant="page"
        title={shown.title}
        status={'tags' in shown && shown.tags
          ? (
            <span className="flex items-center gap-1.5">
              {shown.tags.map((tag) => <Tag key={tag.label} tone={tag.tone}>{tag.label}</Tag>)}
            </span>
          )
          : undefined}
        sub={shown.sub}
        breadcrumbs={[
          /*
            * Not on the settings home itself: a crumb that navigates to the
            * page you are already on is a no-op, and on a phone it is drawn as
            * a back chevron, which makes it a no-op that looks like the way
            * out.
            */
          ...(page || shown.crumbs?.length
            ? [{ label: 'Settings', onClick: () => navigate('/settings') }]
            : []),
          /*
           * A crumb that names a settings page goes to it. The package
           * builder's trail is "Catalog / Packages", and a crumb drawn as a
           * button that does nothing when clicked is worse than no crumb —
           * "Packages" is a tab rather than a page, so it stays inert.
           */
          ...(shown.crumbs ?? []).map((crumb) => {
            if (typeof crumb !== 'string') {
              return { label: crumb.label, onClick: () => navigate(crumb.to) }
            }
            const target = SETTINGS_PAGES.find((one) => one.label === crumb)
            return target
              ? { label: crumb, onClick: () => navigate(`/settings/${target.path}`) }
              : { label: crumb }
          }),
        ]}
        actions={(
          <>
            {/*
              * The way back into the menu on a phone, where there is no rail.
              * Before it, the only route from one settings page to another was
              * back to `/settings` and in again — two taps through a page
              * nobody wanted to look at, and the reason half of settings felt
              * unreachable on a phone.
              */}
            {phone && page && (
              <button
                type="button"
                aria-label="Settings menu"
                onClick={() => setMenu(true)}
                className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-button',
                  'text-text-muted hover:bg-hover')}
              >
                <IconMenu size={18} />
              </button>
            )}
            {slot}
          </>
        )}
      />

      <SettingsNavSheet me={me} open={menu} onClose={() => setMenu(false)} />

      {provide(
        <div className="flex min-h-0 flex-1">
          {/*
            * No 232px nav on a phone — `org-12-phones.html` draws none, and
            * the reason is arithmetic: 232 of 390px is 59% of the screen spent
            * on a list of links that the settings home already is. The catalog
            * got 158px to draw a table in, which truncated every package name
            * to "Annual …". On a phone `/settings` is the menu and the page
            * bar's back chevron is the way back to it.
            */}
          {!wide && !phone && <SettingsNav me={me} />}

          {/*
            * A normal scrolling column, never a fixed-height flex one. §6.4's
            * rule, written here because it applies to every settings page: a
            * taller font or a longer list has to grow the page rather than have
            * its last card cut off.
            */}
          <div className="min-w-0 flex-1 overflow-y-auto">
            <Routes>
              <Route index element={<SettingsHome me={me} />} />
              {/*
                * The builder is its own route under the catalog, not a tab: a
                * package is a screen's worth of structure, and it is listed
                * before the tab route so the router does not swallow it.
                */}
              <Route
                path="catalog/packages/:id"
                element={<Gate me={me} slug="catalog"><PackageBuilder me={me} /></Gate>}
              />
              <Route path="catalog" element={<Gate me={me} slug="catalog"><Catalog me={me} /></Gate>} />
              <Route path="catalog-setup" element={<CatalogSetup me={me} />} />
              <Route path="visit-kinds" element={<VisitKindsSettingsPage me={me} />} />
              <Route path="divisions" element={<Divisions me={me} />} />
              <Route path="equipment-types" element={<EquipmentTypes me={me} />} />
              <Route path="organization" element={<Organization me={me} />} />
              <Route path="profile" element={<CompanyProfilePage me={me} />} />
              {/*
                * What a customer reads on a proposal (`docs/proposal.md`
                * §10.4). The defaults every *new* quote is created with — a
                * quote already built keeps what it was given, which is the
                * whole point of copying at creation.
                */}
              <Route path="proposal" element={<ProposalSettingsPage me={me} />} />
              {/* The build book calls it `/settings/company`; the nav has
                  always said `profile`. Both open the same page rather than
                  one of them being a dead link in somebody's notes. */}
              <Route path="company" element={<Navigate to="/settings/profile" replace />} />
              <Route path="people/*" element={<Gate me={me} slug="people"><People me={me} /></Gate>} />
              <Route path="roles" element={<Roles me={me} />} />
              <Route path="sales-coverage" element={<SalesCoveragePage me={me} />} />
              <Route path="lead-sources" element={<LeadSourcesPage me={me} />} />
              {/* Where customer messages land, and how email is sent (`docs/activity.md` §12.3). */}
              <Route
                path="texts-and-email"
                element={<Gate me={me} slug="texts-and-email"><TextsAndEmailPage me={me} /></Gate>}
              />
              {/* The signature builder (Aaron, Oct 24, note 2.7). */}
              <Route
                path="email-signatures"
                element={<Gate me={me} slug="email-signatures"><EmailSignaturesPage me={me} /></Gate>}
              />
              <Route path=":slug" element={<Placeholder onHome={() => navigate('/settings')} />} />
            </Routes>
          </div>
        </div>,
      )}
    </div>
  )
}

/** A page whose own permission is narrower than the shell's. */
function Gate({ me, slug, children }: {
  me: MeResponse; slug: string; children: React.ReactNode
}) {
  const page = findSettingsPage(slug)
  if (page && !canOpen(me, page)) {
    return (
      <EmptyState
        title={`${page.label} is not yours to open`}
        body="Somebody with the right permission can show it to you, or change yours
              under People → Users & roles."
      />
    )
  }
  return <>{children}</>
}

interface CompanySummary {
  branches: number
  crews: number
  users: number
  featuresOn: number
  /** When the account was opened — the "set up Sep 12" in the bar. */
  setUpOn: string
}

/**
 * The home: who this company is, then one card per group.
 *
 * It used to be seven full-width cards, each holding a grid of buttons badged
 * "soon" — a page about our build order rather than about the company. The
 * mockup's answer is a row of facts and a 3×3 of doors: the group's sentence
 * tells you whether the thing you want is behind it, and the page names are
 * the links, so nothing is a button that looks like a button and does nothing.
 */
function SettingsHome({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<CompanySummary | null>(null)
  const [plans, setPlans] = useState(false)

  useSettingsBar({
    title: 'Company settings',
    sub: summary
      ? `${me.tenant.name} · set up ${formatDocumentDate(summary.setUpOn)}`
      : me.tenant.name,
  })

  useEffect(() => {
    let live = true
    void request<CompanySummary>('/settings/summary')
      .then((payload) => { if (live) setSummary(payload) })
      // A count that will not load is a count we do not print. The row still
      // draws; it just says less.
      .catch(() => undefined)
    return () => { live = false }
  }, [])

  /*
   * Only facts we actually hold. The mockup's line also reads "onboarding
   * finished Sep 12" — there is no onboarding flow in the product yet, so
   * there is no date, and inventing one would make the row a decoration.
   */
  const facts = summary
    ? [
      `${formatNumber(summary.branches)} ${summary.branches === 1 ? 'branch' : 'branches'}`,
      `${formatNumber(summary.crews)} ${summary.crews === 1 ? 'crew' : 'crews'}`,
      `${formatNumber(summary.users)} ${summary.users === 1 ? 'user' : 'users'}`,
      // Nothing writes `tenant_features` until the Features page is built, so
      // a zero here is "nobody has been asked yet" rather than "everything is
      // off" — and printing it would be the decoration this line avoids.
      ...(summary.featuresOn > 0 ? [`${formatNumber(summary.featuresOn)} features on`] : []),
    ].join(' · ')
    : ''

  return (
    <div className="flex max-w-[1100px] flex-col gap-4 px-4 pb-6 pt-5 sm:px-6">
      {/*
        * The company, then the button under it on a phone. Side by side, a
        * 40px avatar and a 150px button left the name 120px and it read
        * "Keystone Groun…" — the one thing on the card nobody should have to
        * guess at.
        */}
      <div className={cn('flex shrink-0 flex-col gap-3 rounded-[12px] border border-line',
        'bg-surface px-3.5 py-3 sm:flex-row sm:items-center')}>
        <span className="flex min-w-0 items-center gap-3">
          <Avatar name={me.tenant.name} size={40} tone="accent" />
          <span className="flex min-w-0 flex-col gap-0.5">
            <b className="truncate text-[14px] font-bold text-text">{me.tenant.name}</b>
            {facts && <span className="truncate text-[12.5px] text-text-muted">{facts}</span>}
          </span>
        </span>
        <span className="flex gap-2 sm:ml-auto">
          <Button className="h-8 whitespace-nowrap text-[12.5px]" onClick={() => setPlans(true)}>
            <IconExternal size={15} />
            Compare plans
          </Button>
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
        {SETTINGS_GROUPS.map((group) => {
          const pages = group.pages.filter((page) => canOpen(me, page))
          if (pages.length === 0) return null
          return (
            <SettingsHomeCard
              key={group.key}
              group={group}
              pages={pages}
              onOpen={(page) => navigate(`/settings/${page.path}`)}
            />
          )
        })}
      </div>

      <PlansModal open={plans} onClose={() => setPlans(false)} />
    </div>
  )
}

function SettingsHomeCard({ group, pages, onOpen }: {
  group: SettingsGroup
  pages: SettingsPage[]
  onOpen: (page: SettingsPage) => void
}) {
  // The group wears its first page's glyph — one picture per idea, and the
  // first page is the one the group is named after.
  const Glyph = pages[0]!.icon

  return (
    <div className={cn('flex min-w-0 shrink-0 flex-col gap-2.5 rounded-record border border-line',
      'bg-surface px-[18px] py-4')}>
      <div className="flex items-center gap-2.5">
        <span className="flex shrink-0 text-accent"><Glyph size={18} /></span>
        <b className="text-[14.5px] font-bold text-text">{group.label}</b>
      </div>
      <span className="text-[12.5px] leading-[1.45] text-text-muted">{group.blurb}</span>
      <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 pt-0.5">
        {pages.map((page) => (
          <button
            key={page.path}
            type="button"
            onClick={() => onOpen(page)}
            className="whitespace-nowrap text-[12.5px] font-semibold text-accent hover:text-accent-dark"
          >
            {page.label}
          </button>
        ))}
      </div>
    </div>
  )
}

interface Tier { key: string; name: string; baseCents: number; seatCents: number; includedSeats: number }

/**
 * What the plans cost.
 *
 * The mockup's tenant row carries "Compare plans" beside "Re-run onboarding".
 * The second is left out — there is no onboarding to re-run — but the first is
 * a real question with a real answer, and `GET /signup/plans` already knows it.
 */
function PlansModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tiers, setTiers] = useState<Tier[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || tiers) return
    void request<{ tiers: Tier[] }>('/signup/plans')
      .then((payload) => setTiers(payload.tiers))
      .catch(() => setError('The plans could not be loaded.'))
  }, [open, tiers])

  return (
    <Modal open={open} onClose={onClose} title="Plans" width={560}>
      {error && <p className="m-0 p-4 text-body text-danger">{error}</p>}
      {!error && !tiers && <p className="m-0 p-4 text-body text-text-muted">Loading…</p>}
      {tiers && (
        <ul className="m-0 flex list-none flex-col p-0">
          {tiers.map((tier) => (
            <li key={tier.key}
              className="flex items-baseline gap-3 border-b border-line-faint px-[18px] py-3 last:border-b-0">
              <b className="text-[13.5px] font-bold text-text">{tier.name}</b>
              <span className="text-[12.5px] text-text-muted">
                {formatCents(tier.baseCents)} a month · {formatNumber(tier.includedSeats)} seats
                included, then {formatCents(tier.seatCents)} each
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}

/**
 * A page that is in the plan and not in the product yet.
 *
 * It says so, and it says what will be on it. The alternative — leaving the
 * row out of the nav — makes the product look as though it has no opinion
 * about tax or routing, which is a worse lie than "not yet".
 */
function Placeholder({ onHome }: { onHome: () => void }) {
  const location = useLocation()
  const slug = location.pathname.replace(/^\/settings\//, '').split('/')[0] ?? ''
  const page = findSettingsPage(slug)
  if (!page) return <Navigate to="/settings" replace />

  return (
    <div className="p-5">
      <EmptyState
        title={`${page.label} is not built yet`}
        body={`${page.blurb}. It is in the plan and it is not here; nothing on this
               screen is hiding behind a permission.`}
        action={{ label: 'Back to settings', onClick: onHome }}
      />
    </div>
  )
}

export type { SettingsPage }
